/**
 * Safely encode a Unicode string to Base64
 * Handles non-ASCII characters (accents, emojis, etc.) that btoa() cannot handle
 * @param str - Unicode string to encode
 * @returns Base64 encoded string
 */

import { logger } from "@utilities/logger";

export function toBase64Unicode(str: string): string {
  return btoa(unescape(encodeURIComponent(str)));
}

/**
 * Create a data URL for WebVTT content with proper Unicode handling
 * @param webvttText - WebVTT formatted subtitle text
 * @returns Data URL string (data:text/vtt;base64,...)
 */
export function createWebVTTDataUrl(webvttText: string): string {
  const base64 = toBase64Unicode(webvttText);
  return `data:text/vtt;base64,${base64}`;
}

/**
 * Convert SRT format to WebVTT format
 * @param srtText - SRT formatted subtitle text
 * @returns WebVTT formatted subtitle text
 */
export function convertSRTToWebVTT(srtText: string): string {
  if (!srtText || !srtText.trim()) {
    return "WEBVTT\n\n";
  }

  const blocks = srtText.trim().split(/\n\s*\n/); // Split by double newlines (cue blocks)
  const webvttLines: string[] = ["WEBVTT", ""];

  for (const block of blocks) {
    const lines = block
      .trim()
      .split("\n")
      .map(line => line.trim())
      .filter(line => line.length > 0);

    if (lines.length < 2) continue; // Need at least sequence number and timestamp

    // Skip sequence number (first line)
    const timestampLine = lines[1];
    const textLines = lines.slice(2);

    if (!textLines.length) continue; // Skip if no text

    // Convert timestamp format from SRT (HH:MM:SS,mmm) to WebVTT (HH:MM:SS.mmm)
    // Also ensure proper format: HH:MM:SS.mmm --> HH:MM:SS.mmm
    let convertedTimestamp = timestampLine.replace(/,/g, ".");

    // Validate timestamp format (should be HH:MM:SS.mmm --> HH:MM:SS.mmm)
    const timestampRegex = /(\d{2}:\d{2}:\d{2}[.,]\d{3})\s*-->\s*(\d{2}:\d{2}:\d{2}[.,]\d{3})/;
    if (!timestampRegex.test(convertedTimestamp)) {
      logger.warn("Invalid timestamp format:", timestampLine);
      continue; // Skip invalid timestamps
    }

    // Normalize to use dots for milliseconds
    convertedTimestamp = convertedTimestamp.replace(/,/g, ".");

    // Add timestamp line
    webvttLines.push(convertedTimestamp + " line:90%"); // this line ensures that the subtitles are above the progressbar
    // Add text lines (join multiple lines with spaces or keep separate)
    textLines.forEach(line => {
      if (line.trim()) {
        webvttLines.push(line.trim());
      }
    });
    // Add empty line between cues
    webvttLines.push("");
  }

  const result = webvttLines.join("\n").trim() + "\n";
  return result;
}
